import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeResponse } from "@zeiterfassung/shared";
import { clearApiCache } from "@/pwa/api-cache";
import { clearPendingLogout, hasPendingLogout, markPendingLogout } from "@/pwa/local-logout";
import { offlineQueue } from "@/pwa/offline-queue";
import { get, isUnauthorized, NetworkError, post } from "../client";
import { queryKeys } from "./keys";

// null = not logged in.
export async function fetchMe(): Promise<MeResponse | null> {
  // A logout that happened offline is finished first; until it succeeds nobody is logged in.
  if (hasPendingLogout()) {
    try {
      await post<{ ok: true }>("/api/auth/logout");
      clearPendingLogout();
    } catch (error) {
      if (error instanceof NetworkError) return null;
      // 401: the session is already gone.
      clearPendingLogout();
    }
  }
  try {
    return await get<MeResponse>("/api/me");
  } catch (error) {
    if (isUnauthorized(error)) {
      // Nobody is logged in: the cached api answers of the last user must not survive. The
      // offline queue stays (it is keyed by user): a session that expired while changes were
      // waiting is replayed once the same employee is back; only a logout wipes it.
      offlineQueue.setUser(null);
      void clearApiCache();
      return null;
    }
    throw error;
  }
}

export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: fetchMe, staleTime: 60_000 });
}

// Offline the server session cannot be ended, but the device is logged out all the same: the
// query cache, the api cache and the offline queue of every user are wiped (shared phones).
export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        const result = await post<{ ok: true }>("/api/auth/logout");
        clearPendingLogout();
        return result;
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error;
        markPendingLogout();
        return { ok: true as const };
      }
    },
    onSettled: async () => {
      offlineQueue.setUser(null);
      await offlineQueue.clear();
      await clearApiCache();
      client.setQueryData(queryKeys.me, null);
      client.clear();
    },
  });
}

export function useLoginWithPassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      post<{ ok: true }>("/api/auth/login", input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (input: { email: string }) => post<{ ok: true }>("/api/auth/magic-link", input),
  });
}

// Extension 3: login by phone number and SMS code.
export function useRequestSmsCode() {
  return useMutation({
    mutationFn: (input: { phone: string }) => post<{ ok: true }>("/api/auth/sms-code", input),
  });
}

export function useVerifySmsCode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone: string; code: string }) =>
      post<{ ok: true }>("/api/auth/sms-verify", input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

export function useVerifyMagicLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string }) => post<{ ok: true }>("/api/auth/verify", input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.me }),
  });
}
